import React from 'react';
import { NextSeo, BreadcrumbJsonLd } from 'next-seo';
import _ from 'lodash';
import { wrapper } from '@/redux/store';
import { Category } from '@/feat-components/Category';
import { axios } from '@/di-container/axios';
import { categoryPageGateway } from '@/di-container/goods/category/client';
import { displayServiceServerGateway } from '@display-component/pc';
import AppLayout from '@/components/AppLayout';
import { DISPLAY_SERVICE_PC_CATEGORY } from '@/constants/display-service';
import {
  buildBreadcrumbJsonLd,
  buildBreadcrumbTrail,
  resolveCategoryHub,
} from '@store/common';
import { fetchBreadcrumbHubName } from '@/utils/breadcrumb-hub';

export interface CategoryMainProps {
  ctgrNo: string;
}

const CategoryMain = (props: any) => {
  /**
   * SEO-354: 브레드크럼의 UI(<a href>) 와 BreadcrumbList 스키마가
   * 같은 배열(trail) 하나에서 파생되도록 SSR 에서 계층을 확정한다.
   */
  const hub = resolveCategoryHub(props?.section);
  const trail = buildBreadcrumbTrail({
    hub,
    hubName: props?.breadcrumbHubName,
    categories: props?.breadcrumbCategories,
  });

  const pageLabelMeta = [
    {
      property: 'section',
      content: props?.pageLabelMeta?.section === 'MALL' ? 'HF' : 'RH' || '',
    },
    {
      property: 'category1_name',
      content: props?.pageLabelMeta?.category1_name || '',
    },
    {
      property: 'category2_name',
      content: props?.pageLabelMeta?.category2_name || '',
    },
    {
      property: 'category3_name',
      content: props?.pageLabelMeta?.category3_name || '',
    },
  ];

  return (
    <>
      <NextSeo
        noindex={process.env.NEXT_PUBLIC_STAGE !== 'production'}
        nofollow={process.env.NEXT_PUBLIC_STAGE !== 'production'}
        title={props?.pageLabelMeta?.title}
        description={props?.pageLabelMeta?.category_description}
        openGraph={{
          description: props?.pageLabelMeta?.category_description,
        }}
        additionalMetaTags={[
          {
            name: 'viewport',
            content:
              'width=device-width,initial-scale=1.0,minimum-scale=1.0,maximum-scale=1.0',
          },
          ...pageLabelMeta,
        ]}
      />
      {/*
        SEO-365: useAppDir 로 <Head> 밖(body)에 렌더한다.
        next/head 영역(next-head-count 카운트 구간)에 <script> 가 있으면 GTM 계열이
        "문서의 첫 script 앞" 에 자기 스크립트를 꽂으면서 마커가 밀리는데 content 는
        갱신되지 않아, 하이드레이션 시 next/head 가 기존 canonical/alternate 를 찾지 못하고
        같은 태그를 다시 삽입한다(복수 canonical). JSON 출력은 useAppDir 여부와 무관하게 동일하다.
      */}
      <BreadcrumbJsonLd
        useAppDir
        itemListElements={buildBreadcrumbJsonLd({
          baseUrl: process.env.NEXT_PUBLIC_WEB_HOST || '',
          trail,
        })}
      />
      <AppLayout
        infoState={true}
        channel={props?.pageLabelMeta?.section}
        content={<Category {...props} trail={trail} />}
      />
    </>
  );
};

export default CategoryMain;

export const getServerSideProps = wrapper.getServerSideProps(
  (store) => async (ctx) => {
    const id = ctx.query.id;

    const categoryNo = _.toNumber(id);

    const isValidateCtgrNo = !_.isNaN(categoryNo) && categoryNo > 0;

    const gateway = isValidateCtgrNo
      ? await displayServiceServerGateway(
          process.env.NEXT_PUBLIC_BASE_API_HOST || '',
          axios,
        ).getTemplateComponents.exec(
          {
            pageIdentificationValue: DISPLAY_SERVICE_PC_CATEGORY,
            categoryNo,
          },
          ctx.req.headers.cookie || '',
        )
      : undefined;

    const currentCategoryResponse = isValidateCtgrNo
      ? await categoryPageGateway().getCurrentCategory.exec({
          categoryNo: id as string,
        })
      : undefined;

    if (!currentCategoryResponse?.data?.categoryLocationMap.categoryNameLarge) {
      return {
        redirect: {
          destination: `/not-exists-content`,
          permanent: false,
        },
      };
    }

    const {
      mallType,
      categoryLevel,
      categoryTitle,
      categoryName,
      categoryNameLarge,
      categoryNoLarge,
      categoryNameMiddle,
      categoryNoMiddle,
      categoryNameSmall,
      categoryNoSmall,
      categoryDescription,
      hanssemGdsDefaultYN,
    } = currentCategoryResponse?.data?.categoryLocationMap;

    /**
     * SEO-354: 브레드크럼 전용 카테고리 배열.
     * 화면에 노출하던 대/중/소 3단을 그대로 사용한다(기존 노출 단계 유지).
     * 무효 항목(번호 0 / 이름 공백) 제외는 buildBreadcrumbTrail 이 담당한다.
     */
    const breadcrumbCategories = [
      { categoryNo: categoryNoLarge, categoryName: categoryNameLarge },
      { categoryNo: categoryNoMiddle, categoryName: categoryNameMiddle },
      { categoryNo: categoryNoSmall, categoryName: categoryNameSmall ?? '' },
    ];

    const defaultTitle =
      (categoryNameSmall || categoryNameMiddle || categoryNameLarge) +
      ' - 한샘몰';

    /**
     * depth가 가장 깊은곳에 있는 카테고리로 title을 만든다.
     */
    const title = categoryTitle || defaultTitle;

    /**
     * SEO-354-1: 허브(사업부) 표시명은 API 가 SSoT 다.
     * 브레드크럼 계층은 SSR 에서 확정되어야 BreadcrumbList 스키마에도 반영된다.
     * 조회 실패 시 null → buildBreadcrumbTrail 이 HUB_NAME 폴백을 쓴다.
     */
    const breadcrumbHubName = await fetchBreadcrumbHubName(
      resolveCategoryHub(mallType),
    );

    const pageParam = Number(ctx.query.page);
    const canonicalUrl =
      process.env.NEXT_PUBLIC_WEB_HOST +
      (pageParam > 1 ? `/category/${id}?page=${pageParam}` : `/category/${id}`);

    return {
      props: {
        data: gateway,
        ctgrNo: id,
        currentCtg: categoryName,
        section: mallType,
        hanssemGdsDefaultYN,
        canonicalUrl,
        breadcrumbCategories,
        breadcrumbHubName,
        pageLabelMeta: {
          section: mallType,
          category_level: categoryLevel,
          category1_no: categoryNoLarge,
          category1_name: categoryNameLarge,
          category2_no: categoryNoMiddle,
          category2_name: categoryNameMiddle,
          category3_no: categoryNoSmall,
          category3_name: categoryNameSmall,
          category_description: categoryDescription,
          title,
        },
        isValidateCtgrNo,
      },
    };
  },
);
